import {
  useState,
} from "react";

import {
  AnimatePresence,
  motion,
} from "framer-motion";

import {
  useLocation,
} from "react-router-dom";

import {
  getLanguageFromPath,
} from "../site/config/routes";

import contactData from "../site/data/contact";

import Footer from "../template/components/layout/Footer";
import HandwrittenEyebrow from "../template/components/common/HandwrittenEyebrow";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  CONTACT PAGE
  ============================================================

  Kolme yhteydenottotapaa:

  01 tapaaminen
  02 tapahtuma
  03 yleinen yhteydenotto

  Kentät vaihtuvat animoidusti valinnan mukaan.

  Lokaalisti submission.endpoint on tyhjä ja lomake toimii
  demo-tilassa.

  WordPress-vaiheessa sama lomake voidaan kytkeä REST API:in
  ilman käyttöliittymän uudelleenrakentamista.
*/

const initialForm = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",

  meetingDate: "",
  meetingTime: "",
  meetingMethod: "video",

  eventName: "",
  participants: "1",
};

export default function ContactPage({ theme = "ivory" }) {
  const location =
    useLocation();

  const language =
    getLanguageFromPath(
      location.pathname
    );

  const page =
    contactData.page;

  const localized =
    (value) => {
      if (
        value === null ||
        value === undefined
      ) {
        return "";
      }

      if (
        typeof value ===
        "string"
      ) {
        return value;
      }

      return (
        value[language] ??
        value.fi ??
        value.en ??
        ""
      );
    };

  const [intent, setIntent] =
    useState("meeting");

  const [form, setForm] =
    useState(initialForm);

  const [status, setStatus] =
    useState("idle");

  const currentIntent =
    page.intents.find(
      (item) =>
        item.id === intent
    ) ??
    page.intents[0];

  const isDemo =
    !String(
      contactData.submission
        ?.endpoint || ""
    ).trim();

  const updateField =
    (event) => {
      const {
        name,
        value,
      } = event.target;

      setForm(
        (current) => ({
          ...current,
          [name]: value,
        })
      );

      if (
        status === "error"
      ) {
        setStatus("idle");
      }
    };

  const selectIntent =
    (nextIntent) => {
      setIntent(nextIntent);
      setStatus("idle");
    };

  const resetForm =
    () => {
      setForm(initialForm);
      setIntent("meeting");
      setStatus("idle");
    };

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      if (
        status === "loading"
      ) {
        return;
      }

      setStatus("loading");

      const payload = {
        intent,
        language,
        ...form,
      };

      /*
        ========================================================
        LOCAL DEMO
        ========================================================

        Pieni viive näyttää oikean lähetyksen käyttäytymisen.

        Stagingissa endpoint korvataan WordPress REST -reitillä.
      */

      if (isDemo) {
        await new Promise(
          (resolve) => {
            window.setTimeout(
              resolve,
              550
            );
          }
        );

        console.info(
          "Contact form demo payload:",
          payload
        );

        setStatus("success");

        return;
      }

      /*
        ========================================================
        REAL ENDPOINT
        ========================================================
      */

      try {
        const response =
          await fetch(
            contactData.submission
              .endpoint,
            {
              method:
                contactData
                  .submission
                  .method ||
                "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );

        if (!response.ok) {
          throw new Error(
            "Contact request failed"
          );
        }

        setStatus("success");
      } catch {
        setStatus("error");
      }
    };

  const fieldClass = `
    w-full
    min-w-0
    border-0
    border-b
    border-border
    bg-transparent
    px-0
    py-4
    text-base
    text-text
    outline-none
    transition-colors
    duration-300
    placeholder:text-muted/55

    focus:border-text
  `;

  const labelClass = `
    block
    text-[9px]
    font-medium
    uppercase
    tracking-[0.22em]
    text-muted

    sm:text-[10px]
  `;

  return (
    <>
      {/* ======================================================
          HERO
          ====================================================== */}

      <section
        className="
          relative
          overflow-hidden
          border-b
          border-border
          bg-canvas
        "
      >
        <motion.div
          className="
            pointer-events-none
            absolute
            inset-y-0
            right-0
            z-0
            w-[58%]
            overflow-hidden

            max-sm:right-[-35%]
            max-sm:w-full
          "
          initial={{
            opacity: 0,
            scale: 1.035,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 1.5,
            delay: 0.15,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
          aria-hidden="true"
        >
          <img
            src="/media/heroes/contact-hero.webp"
            alt=""
            loading="eager"
            decoding="async"
            className="
              h-full
              w-full
              object-cover
              grayscale
              saturate-[0.7]
              contrast-[0.9]
            "
            style={{
              opacity:
                theme === "ivory"
                  ? 0.5
                  : 0.34,
            }}
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-r
              from-canvas
              via-canvas/55
              to-transparent
            "
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-b
              from-canvas/10
              via-transparent
              to-canvas/40
            "
          />
        </motion.div>

        <div
          className="
            relative
            z-10
            mx-auto
            grid
            min-h-[62svh]
            w-full
            max-w-editorial
            items-end
            gap-12
            px-6
            pb-16
            pt-36

            sm:px-10
            sm:pb-20
            sm:pt-44

            lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.34fr)]
            lg:gap-20
            lg:px-16
            lg:pb-24
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.95,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <HandwrittenEyebrow
              label={localized(
                page.hero.eyebrow
              )}
            />

            <h1
              className="
                m-0
                max-w-5xl
                min-w-0
                break-words
                font-display
                text-[clamp(4rem,10vw,9.5rem)]
                font-normal
                leading-[0.84]
                tracking-[-0.055em]
                text-text

                [overflow-wrap:anywhere]
              "
            >
              {localized(
                page.hero.title
              )}
            </h1>
          </motion.div>

          <motion.div
            className="
              min-w-0
              max-w-md
            "
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.85,
              delay: 0.16,
            }}
          >
            <div
              className="
                mb-7
                h-px
                w-10
                bg-text
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                text-sm
                leading-7
                text-muted

                sm:text-base
                sm:leading-8
              "
            >
              {localized(
                page.hero.description
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          CONTACT FORM
          ====================================================== */}

      <section
        className="
          border-b
          border-border
          bg-surface
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-16
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:grid-cols-12
            lg:gap-x-20
            lg:px-16
            lg:py-40
          "
        >
          {/* ==================================================
              LEFT INTRO
              ================================================== */}

          <motion.aside
            className="
              min-w-0

              lg:col-span-4
            "
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.3,
            }}
            transition={{
              duration: 0.85,
            }}
          >
            <div
              className="
                lg:sticky
                lg:top-32
              "
            >
              <p
                className="
                  mb-6
                  text-[9px]
                  font-medium
                  uppercase
                  tracking-[0.28em]
                  text-muted

                  sm:text-[10px]
                "
              >
                {localized(
                  page.form.eyebrow
                )}
              </p>

              <h2
                className="
                  m-0
                  max-w-md
                  font-display
                  text-[clamp(3.2rem,5.5vw,6rem)]
                  font-normal
                  leading-[0.9]
                  tracking-[-0.05em]
                  text-text
                "
              >
                {localized(
                  page.form.title
                )}
              </h2>

              <div
                className="
                  my-8
                  h-px
                  w-10
                  bg-text
                "
                aria-hidden="true"
              />

              <p
                className="
                  m-0
                  max-w-sm
                  text-sm
                  leading-7
                  text-muted

                  sm:text-base
                  sm:leading-8
                "
              >
                {localized(
                  page.form.description
                )}
              </p>

              <div
                className="
                  mt-12
                  hidden
                  border-t
                  border-border

                  lg:block
                "
              >
                {page.intents.map(
                  (item) => (
                    <div
                      key={
                        item.id
                      }
                      className="
                        flex
                        gap-5
                        border-b
                        border-border
                        py-5
                      "
                    >
                      <span
                        className="
                          shrink-0
                          font-display
                          text-sm
                          italic
                          text-muted
                        "
                      >
                        {item.number}
                      </span>

                      <span
                        className="
                          text-sm
                          leading-6
                          text-muted
                        "
                      >
                        {localized(
                          item.title
                        )}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </motion.aside>

          {/* ==================================================
              RIGHT FORM
              ================================================== */}

          <motion.div
            className="
              min-w-0

              lg:col-span-7
              lg:col-start-6
            "
            initial={{
              opacity: 0,
              y: 32,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.15,
            }}
            transition={{
              duration: 0.95,
              delay: 0.06,
            }}
          >
            <AnimatePresence
              mode="wait"
            >
              {status !==
                "success" && (
                <motion.form
                  key="contact-form"
                  onSubmit={
                    handleSubmit
                  }
                  initial={{
                    opacity: 0,
                  }}
                  animate={{
                    opacity: 1,
                  }}
                  exit={{
                    opacity: 0,
                    y: -14,
                  }}
                >
                  {/* ============================================
                      INTENT SELECTOR
                      ============================================ */}

                  <div
                    className="
                      grid
                      gap-3

                      sm:grid-cols-3
                    "
                    role="group"
                    aria-label={
                      language === "en"
                        ? "Type of enquiry"
                        : "Yhteydenoton tyyppi"
                    }
                  >
                    {page.intents.map(
                      (item) => {
                        const active =
                          item.id ===
                          intent;

                        return (
                          <button
                            key={
                              item.id
                            }
                            type="button"
                            onClick={() =>
                              selectIntent(
                                item.id
                              )
                            }
                            className={`
                              group
                              relative
                              min-w-0
                              overflow-hidden
                              border
                              border-border
                              p-5
                              text-left
                              transition-colors
                              duration-300

                              ${
                                active
                                  ? "text-canvas"
                                  : "text-text hover:border-text"
                              }
                            `}
                            aria-pressed={
                              active
                            }
                          >
                            {active && (
                              <motion.span
                                layoutId="contact-intent-background"
                                className="
                                  absolute
                                  inset-0
                                  bg-text
                                "
                                transition={{
                                  type:
                                    "spring",
                                  stiffness:
                                    340,
                                  damping:
                                    34,
                                }}
                                aria-hidden="true"
                              />
                            )}

                            <span
                              className="
                                relative
                                z-10
                                block
                              "
                            >
                              <span
                                className={`
                                  block
                                  text-[9px]
                                  uppercase
                                  tracking-[0.22em]

                                  ${
                                    active
                                      ? "text-canvas/60"
                                      : "text-muted"
                                  }
                                `}
                              >
                                {
                                  item.number
                                }
                              </span>

                              <span
                                className="
                                  mt-5
                                  block
                                  font-display
                                  text-2xl
                                  leading-none
                                  tracking-[-0.035em]
                                "
                              >
                                {localized(
                                  item.title
                                )}
                              </span>

                              <span
                                className={`
                                  mt-4
                                  block
                                  text-xs
                                  leading-5

                                  ${
                                    active
                                      ? "text-canvas/65"
                                      : "text-muted"
                                  }
                                `}
                              >
                                {localized(
                                  item.short
                                )}
                              </span>
                            </span>
                          </button>
                        );
                      }
                    )}
                  </div>

                  {/* ============================================
                      DYNAMIC FIELDS
                      ============================================ */}

                  <div
                    className="
                      mt-14
                      border-t
                      border-border
                      pt-10

                      sm:mt-16
                    "
                  >
                    <AnimatePresence
                      mode="wait"
                    >
                      <motion.div
                        key={intent}
                        initial={{
                          opacity: 0,
                          y: 14,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                        }}
                        exit={{
                          opacity: 0,
                          y: -10,
                        }}
                        transition={{
                          duration: 0.32,
                          ease: [
                            0.22,
                            1,
                            0.36,
                            1,
                          ],
                        }}
                      >
                        <div
                          className="
                            mb-10
                            flex
                            items-end
                            justify-between
                            gap-8
                          "
                        >
                          <div>
                            <p
                              className="
                                m-0
                                text-[9px]
                                uppercase
                                tracking-[0.22em]
                                text-muted
                              "
                            >
                              {
                                currentIntent.number
                              }
                            </p>

                            <h3
                              className="
                                mb-0
                                mt-4
                                font-display
                                text-4xl
                                font-normal
                                leading-none
                                tracking-[-0.04em]
                                text-text

                                sm:text-5xl
                              "
                            >
                              {localized(
                                currentIntent.title
                              )}
                            </h3>
                          </div>

                          <motion.span
                            key={
                              currentIntent.id
                            }
                            initial={{
                              opacity: 0,
                              x: -8,
                            }}
                            animate={{
                              opacity: 1,
                              x: 0,
                            }}
                            className="
                              hidden
                              font-display
                              text-4xl
                              italic
                              text-muted/35

                              sm:block
                            "
                            aria-hidden="true"
                          >
                            {
                              currentIntent.number
                            }
                          </motion.span>
                        </div>

                        {/* MEETING */}

                        {intent ===
                          "meeting" && (
                          <div
                            className="
                              grid
                              gap-x-8
                              gap-y-8

                              sm:grid-cols-2
                            "
                          >
                            <label>
                              <span
                                className={
                                  labelClass
                                }
                              >
                                {localized(
                                  page
                                    .fields
                                    .meetingDate
                                    .label
                                )}
                              </span>

                              <input
                                type="date"
                                name="meetingDate"
                                value={
                                  form.meetingDate
                                }
                                onChange={
                                  updateField
                                }
                                required
                                className={
                                  fieldClass
                                }
                              />
                            </label>

                            <label>
                              <span
                                className={
                                  labelClass
                                }
                              >
                                {localized(
                                  page
                                    .fields
                                    .meetingTime
                                    .label
                                )}
                              </span>

                              <select
                                name="meetingTime"
                                value={
                                  form.meetingTime
                                }
                                onChange={
                                  updateField
                                }
                                required
                                className={
                                  fieldClass
                                }
                              >
                                {page.fields.meetingTime.options.map(
                                  (
                                    option
                                  ) => (
                                    <option
                                      key={
                                        option.id
                                      }
                                      value={
                                        option.id
                                      }
                                    >
                                      {localized(
                                        option.label
                                      )}
                                    </option>
                                  )
                                )}
                              </select>
                            </label>

                            <label
                              className="
                                sm:col-span-2
                              "
                            >
                              <span
                                className={
                                  labelClass
                                }
                              >
                                {localized(
                                  page
                                    .fields
                                    .meetingMethod
                                    .label
                                )}
                              </span>

                              <select
                                name="meetingMethod"
                                value={
                                  form.meetingMethod
                                }
                                onChange={
                                  updateField
                                }
                                className={
                                  fieldClass
                                }
                              >
                                {page.fields.meetingMethod.options.map(
                                  (
                                    option
                                  ) => (
                                    <option
                                      key={
                                        option.id
                                      }
                                      value={
                                        option.id
                                      }
                                    >
                                      {localized(
                                        option.label
                                      )}
                                    </option>
                                  )
                                )}
                              </select>
                            </label>
                          </div>
                        )}

                        {/* EVENT */}

                        {intent ===
                          "event" && (
                          <div
                            className="
                              grid
                              gap-x-8
                              gap-y-8

                              sm:grid-cols-[minmax(0,1fr)_160px]
                            "
                          >
                            <label>
                              <span
                                className={
                                  labelClass
                                }
                              >
                                {localized(
                                  page
                                    .fields
                                    .eventName
                                    .label
                                )}
                              </span>

                              {contactData.events
                                .length >
                              0 ? (
                                <select
                                  name="eventName"
                                  value={
                                    form.eventName
                                  }
                                  onChange={
                                    updateField
                                  }
                                  required
                                  className={
                                    fieldClass
                                  }
                                >
                                  <option value="">
                                    —
                                  </option>

                                  {contactData.events.map(
                                    (
                                      eventItem
                                    ) => (
                                      <option
                                        key={
                                          eventItem.id
                                        }
                                        value={
                                          eventItem.id
                                        }
                                      >
                                        {localized(
                                          eventItem.title
                                        )}
                                      </option>
                                    )
                                  )}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  name="eventName"
                                  value={
                                    form.eventName
                                  }
                                  onChange={
                                    updateField
                                  }
                                  required
                                  placeholder={localized(
                                    page
                                      .fields
                                      .eventName
                                      .placeholder
                                  )}
                                  className={
                                    fieldClass
                                  }
                                />
                              )}
                            </label>

                            <label>
                              <span
                                className={
                                  labelClass
                                }
                              >
                                {localized(
                                  page
                                    .fields
                                    .participants
                                    .label
                                )}
                              </span>

                              <input
                                type="number"
                                min="1"
                                max="99"
                                name="participants"
                                value={
                                  form.participants
                                }
                                onChange={
                                  updateField
                                }
                                required
                                className={
                                  fieldClass
                                }
                              />
                            </label>
                          </div>
                        )}

                        {/* GENERAL */}

                        {intent ===
                          "general" && (
                          <label
                            className="
                              block
                            "
                          >
                            <span
                              className={
                                labelClass
                              }
                            >
                              {localized(
                                page
                                  .fields
                                  .subject
                                  .label
                              )}
                            </span>

                            <input
                              type="text"
                              name="subject"
                              value={
                                form.subject
                              }
                              onChange={
                                updateField
                              }
                              required
                              placeholder={localized(
                                page
                                  .fields
                                  .subject
                                  .placeholder
                              )}
                              className={
                                fieldClass
                              }
                            />
                          </label>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* ============================================
                      COMMON CONTACT DETAILS
                      ============================================ */}

                  <div
                    className="
                      mt-12
                      grid
                      gap-x-8
                      gap-y-9
                      border-t
                      border-border
                      pt-10

                      sm:grid-cols-2
                    "
                  >
                    <label>
                      <span
                        className={
                          labelClass
                        }
                      >
                        {localized(
                          page
                            .fields
                            .name
                            .label
                        )}
                      </span>

                      <input
                        type="text"
                        name="name"
                        autoComplete="name"
                        value={
                          form.name
                        }
                        onChange={
                          updateField
                        }
                        required
                        placeholder={localized(
                          page
                            .fields
                            .name
                            .placeholder
                        )}
                        className={
                          fieldClass
                        }
                      />
                    </label>

                    <label>
                      <span
                        className={
                          labelClass
                        }
                      >
                        {localized(
                          page
                            .fields
                            .email
                            .label
                        )}
                      </span>

                      <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={
                          form.email
                        }
                        onChange={
                          updateField
                        }
                        required
                        placeholder={localized(
                          page
                            .fields
                            .email
                            .placeholder
                        )}
                        className={
                          fieldClass
                        }
                      />
                    </label>

                    <label
                      className="
                        sm:col-span-2
                      "
                    >
                      <span
                        className={
                          labelClass
                        }
                      >
                        {localized(
                          page
                            .fields
                            .phone
                            .label
                        )}
                        {" · "}
                        {localized(
                          page.form
                            .optional
                        )}
                      </span>

                      <input
                        type="tel"
                        name="phone"
                        autoComplete="tel"
                        value={
                          form.phone
                        }
                        onChange={
                          updateField
                        }
                        placeholder={localized(
                          page
                            .fields
                            .phone
                            .placeholder
                        )}
                        className={
                          fieldClass
                        }
                      />
                    </label>

                    <label
                      className="
                        sm:col-span-2
                      "
                    >
                      <span
                        className={
                          labelClass
                        }
                      >
                        {localized(
                          page
                            .fields
                            .message
                            .label
                        )}
                      </span>

                      <textarea
                        name="message"
                        rows="6"
                        value={
                          form.message
                        }
                        onChange={
                          updateField
                        }
                        required
                        placeholder={localized(
                          page
                            .fields
                            .message
                            .placeholder
                        )}
                        className={`
                          ${fieldClass}
                          resize-y
                          leading-7
                        `}
                      />
                    </label>
                  </div>

                  {/* ============================================
                      ERROR
                      ============================================ */}

                  <AnimatePresence>
                    {status ===
                      "error" && (
                      <motion.p
                        initial={{
                          opacity: 0,
                          y: 8,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                        }}
                        exit={{
                          opacity: 0,
                        }}
                        role="alert"
                        className="
                          mb-0
                          mt-8
                          border-l
                          border-text
                          pl-4
                          text-sm
                          leading-6
                          text-text
                        "
                      >
                        {localized(
                          page.error
                        )}
                      </motion.p>
                    )}
                  </AnimatePresence>

                  {/* ============================================
                      SUBMIT
                      ============================================ */}

                  <div
                    className="
                      mt-12
                      flex
                      items-center
                      justify-between
                      gap-8
                      border-t
                      border-border
                      pt-8
                    "
                  >
                    <span
                      className="
                        hidden
                        text-[9px]
                        uppercase
                        tracking-[0.2em]
                        text-muted

                        sm:block
                      "
                    >
                      02 /{" "}
                      {localized(
                        page.submit
                          .idle
                      )}
                    </span>

                    <motion.button
                      type="submit"
                      disabled={
                        status ===
                        "loading"
                      }
                      whileHover={{
                        x: 3,
                      }}
                      whileTap={{
                        scale: 0.985,
                      }}
                      className="
                        group
                        ml-auto
                        inline-flex
                        min-h-14
                        items-center
                        justify-center
                        gap-5
                        bg-text
                        px-7
                        text-[10px]
                        font-semibold
                        uppercase
                        tracking-[0.2em]
                        text-canvas
                        transition-opacity

                        disabled:cursor-wait
                        disabled:opacity-60
                      "
                    >
                      {status ===
                      "loading"
                        ? localized(
                            page.submit
                              .loading
                          )
                        : localized(
                            page.submit
                              .idle
                          )}

                      <span
                        className="
                          transition-transform
                          duration-300
                          group-hover:translate-x-1
                        "
                        aria-hidden="true"
                      >
                        →
                      </span>
                    </motion.button>
                  </div>
                </motion.form>
              )}

              {/* ================================================
                  SUCCESS
                  ================================================ */}

              {status ===
                "success" && (
                <motion.div
                  key="contact-success"
                  initial={{
                    opacity: 0,
                    y: 24,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: [
                      0.22,
                      1,
                      0.36,
                      1,
                    ],
                  }}
                  className="
                    flex
                    min-h-[520px]
                    flex-col
                    justify-center
                    border-y
                    border-border
                    py-16
                  "
                  aria-live="polite"
                >
                  <motion.span
                    initial={{
                      scaleX: 0,
                    }}
                    animate={{
                      scaleX: 1,
                    }}
                    transition={{
                      duration: 0.8,
                      delay: 0.1,
                    }}
                    className="
                      mb-10
                      h-px
                      w-16
                      origin-left
                      bg-text
                    "
                  />

                  <p
                    className="
                      mb-6
                      text-[9px]
                      uppercase
                      tracking-[0.28em]
                      text-muted

                      sm:text-[10px]
                    "
                  >
                    {localized(
                      page.success
                        .eyebrow
                    )}
                  </p>

                  <h2
                    className="
                      m-0
                      max-w-2xl
                      font-display
                      text-[clamp(3.4rem,6vw,6.5rem)]
                      font-normal
                      leading-[0.9]
                      tracking-[-0.05em]
                      text-text
                    "
                  >
                    {localized(
                      page.success
                        .title
                    )}
                  </h2>

                  <p
                    className="
                      mb-0
                      mt-9
                      max-w-xl
                      text-base
                      leading-8
                      text-muted
                    "
                  >
                    {localized(
                      page.success
                        .text
                    )}
                  </p>

                  {isDemo && (
                    <p
                      className="
                        mb-0
                        mt-5
                        max-w-xl
                        text-xs
                        leading-6
                        text-muted/60
                      "
                    >
                      {localized(
                        page.success
                          .demo
                      )}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={
                      resetForm
                    }
                    className="
                      group
                      mt-10
                      inline-flex
                      w-fit
                      items-center
                      gap-4
                      border-b
                      border-text
                      bg-transparent
                      pb-2
                      text-[10px]
                      font-semibold
                      uppercase
                      tracking-[0.2em]
                      text-text
                    "
                  >
                    {localized(
                      page.success
                        .another
                    )}

                    <span
                      className="
                        transition-transform
                        duration-300
                        group-hover:translate-x-1
                      "
                      aria-hidden="true"
                    >
                      →
                    </span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <Footer
        language={language}
      />
    </>
  );
}
